// The patient outreach endpoints, in one place.
//
// W1 owns the routes (backend/app/routes/outreach.py); the contract is the outreach
// PR-1 contract (docs/outreach.md once it lands). Every path is practice-scoped,
// every amount is integer CENTS, and every field name the Outreach card reads is
// named in this file and nowhere else. CSRF is attached by the wrapper the app
// installs on window.fetch, so a plain fetch is correct.
//
// Nothing in this module sends anything on its own. `sendMessage` is called from one
// place — the Send button of the composer — and only after a preview of the same
// text has been shown; it posts `confirm: true`, which the server requires.
//
// PR 2 adds the call panel: `fetchCallScript` renders a call_script template for staff
// to read aloud (it writes nothing and is never sent to the patient), and `logCall`
// records the outcome as an outreach_messages row with channel `call`.

import {
  formatDay,
  formatWhen,
  kindLabel,
  outcomeLabel,
  sendStatusChip,
} from "../collections/api";

import { formatPhoneDisplay } from "../../utils/formatPhone";

export { formatDay, formatWhen, kindLabel, outcomeLabel, sendStatusChip, formatPhoneDisplay };

const API_URL = process.env.REACT_APP_API_URL || "";

// practice_patients.sms_consent / email_consent (models_outreach.CONSENT_VALUES).
export const CONSENT_VALUES = [
  { key: "unknown", label: "Unknown" },
  { key: "yes", label: "Yes" },
  { key: "no", label: "No" },
];

// The two channels the composer offers. Calls are not composed: the Call panel
// renders a script and logs the outcome (`logCall`), which lands in the thread as a
// message with channel `call`.
export const CHANNELS = [
  { key: "sms", label: "SMS" },
  { key: "email", label: "Email" },
];

export const channelLabel = (channel) =>
  ({ sms: "SMS", email: "Email", call: "Call" })[channel] || channel || "—";

// outreach_messages.status → what the chip says and how it is coloured.
const MESSAGE_STATUS = {
  queued: { label: "Queued", tone: "muted" },
  sent: { label: "Sent", tone: "info" },
  delivered: { label: "Delivered", tone: "good" },
  failed: { label: "Failed", tone: "bad" },
  received: { label: "Received", tone: "info" },
  logged: { label: "Logged", tone: "muted" },
};

export function messageStatusChip(status) {
  return MESSAGE_STATUS[status] || { label: status || "Unknown", tone: "muted" };
}

export const directionLabel = (direction) =>
  direction === "in" ? "From patient" : "To patient";

// models_outreach.CALL_OUTCOMES, in the order the form offers them. The tone is the
// chip's colour in the thread: a reached patient or a promise is progress, a voicemail
// or no answer is neutral, a wrong number or a dispute needs someone to act.
export const CALL_OUTCOMES = [
  { key: "reached", label: "Reached", tone: "good" },
  { key: "voicemail", label: "Voicemail left", tone: "muted" },
  { key: "no_answer", label: "No answer", tone: "muted" },
  { key: "wrong_number", label: "Wrong number", tone: "bad" },
  { key: "promised", label: "Promised to pay by…", tone: "good" },
  { key: "disputed", label: "Disputes the balance", tone: "bad" },
];

const CALL_OUTCOME_BY_KEY = Object.fromEntries(CALL_OUTCOMES.map((o) => [o.key, o]));

// An unknown outcome is shown as the server sent it, never guessed at.
export const callOutcomeLabel = (outcome) =>
  (CALL_OUTCOME_BY_KEY[outcome] || {}).label || outcome || "—";

export const callOutcomeTone = (outcome) =>
  (CALL_OUTCOME_BY_KEY[outcome] || {}).tone || "muted";

// Integer cents → "$1,234.56". Not the ledger's formatCurrency, which renders a
// hyphen for zero: on a balance line "$0.00" is the honest answer.
export function formatCents(cents) {
  const n = Number(cents);
  if (cents == null || !Number.isFinite(n)) return "—";
  return (
    "$" +
    (n / 100).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

// Never surface a server message we did not write. W1 returns {error: "..."} for
// the refusals a biller can act on and a generic sentence for anything else.
async function readError(response, fallback) {
  try {
    const body = await response.json();
    return { message: (body && body.error) || fallback, body: body || {} };
  } catch (err) {
    return { message: fallback, body: {} };
  }
}

async function request(url, options, fallback) {
  const response = await fetch(url, { credentials: "include", ...options });
  if (!response.ok) {
    const { message, body } = await readError(response, fallback);
    const error = new Error(message);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  const body = await response.json().catch(() => null);
  if (!body || typeof body !== "object") {
    const error = new Error(
      "The response could not be read, so this may be incomplete. Try again.",
    );
    error.status = response.status;
    error.body = {};
    throw error;
  }
  return body;
}

function withBody(method, payload) {
  return {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
}

const base = (practiceId) => `${API_URL}/api/practices/${practiceId}/outreach`;
const patientBase = (practiceId, patientId) =>
  `${base(practiceId)}/patients/${encodeURIComponent(patientId)}`;

const num = (value) => {
  const n = Number(value);
  return value == null || value === "" || !Number.isFinite(n) ? null : n;
};
const str = (value) => (typeof value === "string" ? value : null);

// ---- status ----

// GET …/status. Only an explicit true configures a channel: a body we cannot read
// never offers one.
export async function fetchStatus(practiceId) {
  const body = await request(
    `${base(practiceId)}/status`,
    {},
    "Could not check whether texting and email are set up.",
  );
  return {
    smsConfigured: body.sms_configured === true,
    emailConfigured: body.email_configured === true,
    dryRun: body.dry_run === true,
    practicePhone: str(body.practice_phone),
  };
}

// ---- templates ----

export function normalizeTemplate(raw) {
  const t = raw || {};
  return {
    id: t.id,
    practiceId: t.practice_id == null ? null : t.practice_id,
    scope: t.scope || (t.practice_id ? "practice" : "global"),
    kind: t.kind || null,
    name: t.name || "",
    subject: str(t.subject),
    body: str(t.body) || "",
    enabled: t.enabled !== false,
  };
}

// GET …/templates?kind=sms|email|call_script → the practice's own first, then the
// global defaults, in the server's order.
export async function fetchTemplates(practiceId, kind) {
  const qs = new URLSearchParams({ kind });
  const body = await request(
    `${base(practiceId)}/templates?${qs.toString()}`,
    {},
    "Could not load the message templates.",
  );
  if (!Array.isArray(body.templates)) {
    throw new Error(
      "The response could not be read, so the templates may be incomplete. Try again.",
    );
  }
  return body.templates.map(normalizeTemplate);
}

// ---- summary and contact fields ----

export const CONTACT_FIELDS = [
  "phone_mobile",
  "phone_home",
  "email",
  "sms_consent",
  "email_consent",
  "preferred_language",
];

export function normalizePatient(raw) {
  const p = raw || {};
  return {
    id: p.id,
    firstName: str(p.first_name),
    displayName: str(p.display_name),
    phoneMobile: str(p.phone_mobile),
    phoneHome: str(p.phone_home),
    phone: str(p.phone),
    email: str(p.email),
    smsConsent: p.sms_consent || "unknown",
    emailConsent: p.email_consent || "unknown",
    smsOptedOutAt: str(p.sms_opted_out_at),
    preferredLanguage: str(p.preferred_language),
    // The latest promise-to-pay date (YYYY-MM-DD) from a logged call, kept until a
    // payment or a newer promise replaces it.
    promisedPayBy: str(p.promised_pay_by),
  };
}

export function normalizeSummary(raw) {
  const s = raw || {};
  const balance = s.balance || {};
  const statement = s.last_statement || null;
  const outreach = s.last_outreach || null;
  const call = s.last_call || null;
  return {
    patient: normalizePatient(s.patient),
    balance: {
      patientBalanceCents: num(balance.patient_balance_cents) ?? 0,
      oldestDos: str(balance.oldest_dos),
      openClaims: num(balance.open_claims) ?? 0,
    },
    lastStatement: statement
      ? { sentAt: str(statement.sent_at), status: str(statement.status) }
      : null,
    lastOutreach: outreach
      ? { at: str(outreach.at), channel: str(outreach.channel) }
      : null,
    lastCall: call ? { at: str(call.at), outcome: str(call.outcome) } : null,
    // Only an explicit true may text or email; a body we cannot read never enables
    // a channel.
    canText: s.can_text === true,
    canTextReason: str(s.can_text_reason),
    canEmail: s.can_email === true,
    canEmailReason: str(s.can_email_reason),
  };
}

export async function fetchSummary(practiceId, patientId) {
  const body = await request(
    `${patientBase(practiceId, patientId)}/summary`,
    {},
    "Could not load this patient's outreach summary.",
  );
  return normalizeSummary(body);
}

// PUT …/contact with ONLY the fields that changed (the server writes an event naming
// the changed field names, so posting unchanged ones would record edits nobody made).
// `fields` is keyed by the wire names in CONTACT_FIELDS. Returns the patient block.
export async function saveContact(practiceId, patientId, fields) {
  const payload = {};
  CONTACT_FIELDS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(fields, key)) {
      const value = fields[key];
      payload[key] = typeof value === "string" ? value.trim() : value;
    }
  });
  if (Object.keys(payload).length === 0) {
    throw new Error("Nothing has changed.");
  }
  const body = await request(
    `${patientBase(practiceId, patientId)}/contact`,
    withBody("PUT", payload),
    "Could not save the contact details.",
  );
  // The contract says the response is the summary's `patient` block; accept it bare
  // or wrapped, so a wrapped answer does not read as a patient with no fields.
  return normalizePatient(body.patient || body);
}

// ---- thread ----

export function normalizeThreadItem(raw) {
  const r = raw || {};
  const type = r.type || "message";
  if (type === "contact") {
    return {
      type,
      id: r.id,
      key: `contact-${r.id}`,
      at: str(r.at),
      kind: r.kind || null,
      outcome: r.outcome || null,
      note: str(r.note),
      byUser: str(r.by_user),
      nextContactOn: str(r.next_contact_on),
    };
  }
  if (type === "statement") {
    return {
      type,
      id: r.id,
      key: `statement-${r.id}`,
      at: str(r.at) || str(r.sent_at),
      sentAt: str(r.sent_at),
      status: r.status || null,
    };
  }
  return {
    type: "message",
    id: r.id,
    key: `message-${r.id}`,
    at: str(r.at) || str(r.created_at),
    channel: r.channel || null,
    direction: r.direction || "out",
    templateId: r.template_id == null ? null : r.template_id,
    subject: str(r.subject),
    body: str(r.body) || "",
    toAddressMasked: str(r.to_address_masked),
    status: r.status || null,
    error: str(r.error),
    sentBy: str(r.sent_by),
    createdAt: str(r.created_at),
    statusAt: str(r.status_at),
    // A logged call (channel "call", status "logged"): the outcome and, for a
    // promise, the date. `body` is the biller's note, never the script.
    outcome: str(r.outcome),
    promisedBy: str(r.promised_by),
  };
}

// GET …/thread → items newest LAST, in the server's order (messages, call notes and
// statement sends interleaved by `at`).
export async function fetchThread(practiceId, patientId) {
  const body = await request(
    `${patientBase(practiceId, patientId)}/thread`,
    {},
    "Could not load the outreach history.",
  );
  if (!Array.isArray(body.items)) {
    throw new Error(
      "The response could not be read, so the history may be incomplete. Try again.",
    );
  }
  return body.items.map(normalizeThreadItem);
}

// ---- preview and send ----

function composePayload({ channel, templateId, body, subject }) {
  const payload = { channel };
  if (templateId != null && templateId !== "") payload.template_id = Number(templateId);
  if (typeof body === "string") payload.body = body;
  if (channel === "email" && typeof subject === "string") payload.subject = subject;
  return payload;
}

// POST …/preview → the rendered message. NEVER sends.
export async function previewMessage(practiceId, patientId, draft) {
  const body = await request(
    `${patientBase(practiceId, patientId)}/preview`,
    withBody("POST", composePayload(draft)),
    "Could not build the preview.",
  );
  return {
    channel: body.channel || draft.channel,
    subject: str(body.subject),
    body: str(body.body) || "",
    toAddressMasked: str(body.to_address_masked),
    chars: num(body.chars),
    segments: num(body.segments),
    // Only an explicit true may send; a body we cannot read never enables the button.
    canSend: body.can_send === true,
    reason: str(body.reason),
  };
}

// POST …/send {…draft, confirm: true} → 201 {message}. Errors carry `status`: 409 is a
// consent refusal, 503 an unconfigured channel; both carry the server's sentence.
export async function sendMessage(practiceId, patientId, draft) {
  const body = await request(
    `${patientBase(practiceId, patientId)}/send`,
    withBody("POST", { ...composePayload(draft), confirm: true }),
    "The message could not be sent.",
  );
  return body.message
    ? normalizeThreadItem({ type: "message", ...body.message })
    : null;
}

// ---- calls ----

// POST …/call-script {template_id} → the script rendered with this patient's figures,
// for staff to read aloud. Writes nothing; nothing reaches the patient. `tel` is the
// E.164 mobile or null, with `telReason` saying why when null.
export async function fetchCallScript(practiceId, patientId, templateId) {
  const body = await request(
    `${patientBase(practiceId, patientId)}/call-script`,
    withBody("POST", { template_id: Number(templateId) }),
    "Could not build the call script.",
  );
  const fields = body.fields || {};
  return {
    templateId: body.template_id == null ? null : body.template_id,
    name: str(body.name) || "",
    body: str(body.body) || "",
    tel: str(body.tel),
    telReason: str(body.tel_reason),
    fields: {
      balance: str(fields.balance),
      oldestDosMonth: str(fields.oldest_dos_month),
      lastStatementDate: str(fields.last_statement_date),
      paymentOptions: str(fields.payment_options),
    },
  };
}

// POST …/call {outcome, note, promised_by, script_template_id} → 201 {message, patient}.
// `promised_by` goes only with outcome "promised" (the server requires it there and
// refuses it elsewhere); `script_template_id` is attribution for the script read.
export async function logCall(
  practiceId,
  patientId,
  { outcome, note, promisedBy, scriptTemplateId },
) {
  const payload = { outcome, note: typeof note === "string" ? note.trim() : "" };
  if (outcome === "promised" && promisedBy) payload.promised_by = promisedBy;
  if (scriptTemplateId != null && scriptTemplateId !== "") {
    payload.script_template_id = Number(scriptTemplateId);
  }
  const body = await request(
    `${patientBase(practiceId, patientId)}/call`,
    withBody("POST", payload),
    "The call could not be logged.",
  );
  return {
    message: body.message
      ? normalizeThreadItem({ type: "message", ...body.message })
      : null,
    patient: body.patient ? normalizePatient(body.patient) : null,
  };
}
